import { Button, Card, Container, Grid, Stack, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import BrudCrumbs from '../../components/BreadCrumbs';
import Page from '../../components/Page';
import PageTitle from '../../components/PageHeading';
import CustomInput from '../../components/CustomInput';
import CustomSelect from '../../components/CustomSelect';

import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import {
  getAccessLevelData,
  getCityData,
  getDepartmentList,
  getStateData,
  saveApiData,
  setCityData
} from '../../store/Slice/Global/GlobalSlice';
import { UserApi } from '../../utils/Apis';
import useGlobalSelector from '../../store/Slice/Global/GlobalSelector';
import useOnboardingSelector from '../../store/Slice/Onboarding/OnboardingSelector';
import { getReportingManagerList2 } from '../../store/Slice/Onboarding/OnboardingSlice';
import useUserSelector from '../../store/Slice/User/UserSelector';
import { getUserViewList } from '../../store/Slice/User/UserSlice';

const P = '#13519d';

function AddEditUser() {
  const {
    handleSubmit: handleReduxSubmit,
    control,
    setValue,
    reset,
    watch,
    formState: { errors }
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      mobile: '',
      gender: '',
      typeId: '',
      accessLevelId: '',
      reportingManagerId: '',
      departmentName: '',
      stateId: '',
      cityName: '',
      userName: ''
    }
  });

  const { rowId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { LoginData } = useLoginSelector();
  const { AccessLevelList, stateDrop, CityDrop, DepartmentListDrop } = useGlobalSelector();
  const { ReportingManagerListData2 } = useOnboardingSelector();
  const { UserViewListData } = useUserSelector();

  const selectedCity = watch('cityName');

  const userTypeMap = {
    'Employee User': 304,
    'Partner User': 302
  };

  // Fetch initial dropdown and user data
  useEffect(() => {
    if (!stateDrop?.length) {
      dispatch(getStateData({ countryId: 101 }));
    }
    if (LoginData?.companyId) {
      if (!AccessLevelList?.length) {
        dispatch(getAccessLevelData(LoginData?.companyId));
      }
      if (!DepartmentListDrop?.length) {
        dispatch(getDepartmentList({ companyId: LoginData?.companyId }));
      }
    }
  }, [dispatch, LoginData, stateDrop, AccessLevelList, DepartmentListDrop]);

  useEffect(() => {
    if (rowId && LoginData?.userId) {
      const endpoint = `${UserApi?.viewUser}/${rowId}?loggedInUserId=${LoginData?.userId}`;
      dispatch(getUserViewList(endpoint));
    }
  }, [rowId, LoginData?.userId, dispatch]);

  // City API
  const handleGetCity = useCallback(
    (stateValue) => {
      const stateId = stateValue?.value ?? stateValue;

      if (!stateId) {
        dispatch(setCityData([]));
        setValue('cityName', null);
        return;
      }

      setValue('stateId', stateId);

      dispatch(
        getCityData({
          stateId,
          searchLiteral: ''
        })
      );
    },
    [dispatch, setValue]
  );

  /* -------------------- PREFILL ON EDIT -------------------- */
  useEffect(() => {
    if (!rowId || !UserViewListData || !stateDrop?.length) return;

    const data = UserViewListData;

    const userStateId =
      UserViewListData?.stateName ??
      UserViewListData?.stateId ??
      UserViewListData?.province ??
      null;

    const selectedState =
      stateDrop.find((s) => String(s.label) === String(userStateId)) ||
      stateDrop.find(
        (s) =>
          String(s.label || '')
            .trim()
            .toLowerCase() ===
          String(UserViewListData?.stateName || '')
            .trim()
            .toLowerCase()
      );

    reset({
      firstName: data.firstName || '',
      lastName: data.lastName || '',
      email: data.email || '',
      mobile: data.mobile || '',
      gender: data.gender || '',
      typeId: userTypeMap[data.userType] || '',
      accessLevelId: Number(data.accessLevelId) || '',
      userName: data.userName || '',
      stateId: selectedState?.label || '',
      cityName: '',
      departmentName: data.departmentId ?? data.departmentName ?? '',
      reportingManagerId: data?.reportingManagerId ?? data.reportingManagerId ?? ''
    });

    // Load city list AFTER reset
    if (selectedState?.value) {
      setValue('stateId', selectedState.value);
      dispatch(
        getCityData({
          stateId: selectedState.value,
          searchLiteral: ''
        })
      );
    }
  }, [rowId, UserViewListData, stateDrop, reset, dispatch]);

  /* -------------------- SUBMIT -------------------- */
  const onSubmit = useCallback(
    async (data) => {
      const payload = {
        userId: rowId ? Number(rowId) : null,
        firstName: data?.firstName,
        lastName: data?.lastName,
        email: data?.email,
        mobile: data?.mobile,
        gender: data?.gender,
        typeId: Number(data?.typeId),
        accessLevelId: Number(data?.accessLevelId),
        reportingManagerId: data?.reportingManagerId,
        stateId: Number(data?.stateId ?? null),
        cityId: Number(
          (data?.cityName && (data.cityName.value ?? data.cityName)) ?? data?.cityId ?? null
        ),
        departmentId: Number(data.departmentName || data.departmentId || 41),
        loggedInUserId: LoginData?.userId || '',
        companyId: LoginData?.companyId,
        startDate: '2025-12-15'
      };

      const res = await dispatch(saveApiData(UserApi?.addUpdateUser, payload));

      if (res?.status === 'success') {
        navigate('/users/');
      }
    },
    [LoginData, rowId, dispatch, navigate]
  );

  const reportingMangerTwoApi = useCallback(() => {
    const credential = {
      userId: LoginData?.userId
    };
    dispatch(getReportingManagerList2(credential));
  }, [LoginData?.userId, dispatch]);

  useEffect(() => {
    reportingMangerTwoApi();
  }, [reportingMangerTwoApi]);

  useEffect(() => {
    if (!rowId && !selectedCity) return;
    if (!ReportingManagerListData2?.length) return;

    if (rowId && UserViewListData?.relationshipManagerId) {
      const rmExists = ReportingManagerListData2.some(
        (rm) => rm.userId === UserViewListData.relationshipManagerId
      );

      if (rmExists) {
        setValue('relationshipManagerId', UserViewListData.relationshipManagerId);
      }
    }
  }, [rowId, selectedCity, ReportingManagerListData2, UserViewListData, setValue]);

  useEffect(() => {
    if (!rowId) return;
    if (!UserViewListData || !CityDrop?.length) return;

    const cityId = UserViewListData.cityId ?? UserViewListData.cityName;
    if (cityId === undefined || cityId === null) return;

    const match =
      CityDrop.find((c) => String(c.value) === String(cityId)) ||
      CityDrop.find(
        (c) =>
          String(c.label || '')
            .trim()
            .toLowerCase() ===
          String(UserViewListData.cityName || '')
            .trim()
            .toLowerCase()
      );

    if (match) {
      setValue('cityName', match.value ?? match);
    }
  }, [CityDrop, UserViewListData, setValue, rowId]);

  useEffect(() => {
    if (!rowId) return;
    if (!UserViewListData || !DepartmentListDrop?.length) return;

    const depId = UserViewListData.departmentId ?? UserViewListData.departmentName;
    if (depId === undefined || depId === null) return;

    const firstOpt = DepartmentListDrop[0];
    if (firstOpt && typeof firstOpt === 'object' && 'value' in firstOpt) {
      const match = DepartmentListDrop.find(
        (d) => String(d.value) === String(depId) || String(d.label) === String(depId)
      );
      if (match) setValue('departmentName', match.value);
    } else {
      const match = DepartmentListDrop.find((d) => String(d) === String(depId));
      if (match) setValue('departmentName', match);
    }
  }, [rowId, UserViewListData, DepartmentListDrop, setValue]);

  return (
    <Page title="Kpros || User">
      <Container maxWidth="xl">
        <Box pt={3} pb={3} mb={4}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/users')}
                sx={{
                  color: '#64748b',
                  borderColor: '#cbd5e1',
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: '10px',
                  '&:hover': {
                    borderColor: '#94a3b8',
                    bgcolor: '#f1f5f9'
                  }
                }}
                variant="outlined"
              >
                Back
              </Button>
              <PageTitle info={rowId ? 'Edit User' : 'Add New User'} />
            </Box>
            <Box>
              <BrudCrumbs />
            </Box>
          </Stack>

          <Card
            sx={{
              borderRadius: '20px',
              overflow: 'hidden',
              boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
              border: '1px solid #e2e8f0',
              mb: 3
            }}
          >
            <Box
              sx={{
                background: `linear-gradient(135deg, ${P} 0%, #1565c0 100%)`,
                px: { xs: 2.5, sm: 3.5 },
                py: { xs: 2.5, sm: 3 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: '1.15rem', sm: '1.35rem' },
                    color: '#fff'
                  }}
                >
                  {rowId ? 'Edit User Details' : 'Fill User Details'}
                </Typography>
                <Typography
                  sx={{
                    color: 'rgba(255, 255, 255, 0.85)',
                    fontSize: '0.85rem',
                    mt: 0.5
                  }}
                >
                  {rowId
                    ? 'Update profile information and role permissions for this user'
                    : 'Fill in the information below to create a new user account'}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ p: { xs: 2.5, sm: 4 }, bgcolor: '#fff' }}>
              <form id="addEditUser" onSubmit={handleReduxSubmit(onSubmit)}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={4}>
                    <CustomInput
                      name="firstName"
                      label="First Name"
                      control={control}
                      errors={errors}
                      isRequired
                      fieldCode="NAME"
                      fieldType="ALPHABET"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <CustomInput
                      name="lastName"
                      label="Last Name"
                      control={control}
                      errors={errors}
                      isRequired
                      fieldCode="NAME"
                      fieldType="ALPHABET"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <CustomInput
                      name="email"
                      label="Email"
                      control={control}
                      errors={errors}
                      isRequired
                      fieldCode="EMAIL"
                      fieldType="EMAIL"
                      disabled={Boolean(rowId)}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <CustomInput
                      name="mobile"
                      label="Mobile"
                      control={control}
                      errors={errors}
                      isRequired
                      fieldCode="MOBILE"
                      fieldType="NUMBER"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <CustomSelect
                      name="gender"
                      label="Gender"
                      control={control}
                      errors={errors}
                      isRequired
                      options={[
                        { id: 'MALE', value: 'MALE', label: 'Male' },
                        { id: 'FEMALE', value: 'FEMALE', label: 'Female' }
                      ]}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <CustomSelect
                      name="departmentName"
                      label="Department"
                      control={control}
                      errors={errors}
                      isRequired
                      options={DepartmentListDrop || []}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <CustomSelect
                      name="stateId"
                      label="State"
                      control={control}
                      errors={errors}
                      options={stateDrop || []}
                      onChange={(event, val) => {
                        handleGetCity(val);
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <CustomSelect
                      name="cityName"
                      label="City"
                      control={control}
                      errors={errors}
                      options={CityDrop || []}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <CustomSelect
                      name="reportingManagerId"
                      label="Reporting Manager"
                      control={control}
                      errors={errors}
                      options={
                        ReportingManagerListData2?.map((user) => ({
                          value: user?.userId,
                          label: user?.userName || user?.fullName
                        })) || []
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <CustomSelect
                      name="typeId"
                      label="User Type"
                      control={control}
                      errors={errors}
                      isRequired
                      options={[
                        { id: 304, value: 304, label: 'Employee' },
                        { id: 302, value: 302, label: 'Partner' }
                      ]}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <CustomSelect
                      name="accessLevelId"
                      label="Access Level"
                      control={control}
                      errors={errors}
                      isRequired
                      options={
                        Array.isArray(AccessLevelList)
                          ? AccessLevelList.map((opt) => ({
                              label:
                                opt?.accessLevelName ??
                                opt?.name ??
                                opt?.label ??
                                opt?.value ??
                                String(opt),
                              value: opt?.accessLevelId ?? opt?.id ?? opt?.value ?? opt
                            }))
                          : []
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <CustomInput
                      name="userName"
                      label="Username"
                      control={control}
                      errors={errors}
                    />
                  </Grid>
                </Grid>
              </form>
            </Box>
          </Card>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
              mt: 2
            }}
          >
            <Button
              variant="outlined"
              onClick={() => navigate('/users')}
              sx={{
                color: '#64748b',
                borderColor: '#cbd5e1',
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1,
                borderRadius: '10px',
                '&:hover': {
                  borderColor: '#94a3b8',
                  bgcolor: '#f1f5f9'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              type="submit"
              form="addEditUser"
              sx={{
                bgcolor: P,
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 700,
                px: 4,
                py: 1.2,
                fontSize: '0.95rem',
                boxShadow: '0 4px 14px rgba(19, 81, 157, 0.25)',
                '&:hover': {
                  bgcolor: '#0a3870',
                  boxShadow: '0 6px 18px rgba(19, 81, 157, 0.35)'
                }
              }}
            >
              {rowId ? 'Update User' : 'Save User'}
            </Button>
          </Box>
        </Box>
      </Container>
    </Page>
  );
}

export default AddEditUser;
